import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('marquee')) {
      this.#setupMarquee();
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
    this.#marqueeAnimation?.cancel();
    cancelAnimationFrame(this.#fillFrame);
  }

  #setupMarquee() {
    const group = this.querySelector('.announcement-bar__slides');
    if (!group) return;

    // Show all slides (JS normally hides non-active ones via aria-hidden).
    (this.refs.slides ?? []).forEach((slide) => slide.setAttribute('aria-hidden', 'false'));

    // Wrap the announcements in a track that scrolls as one strip.
    const track = document.createElement('div');
    track.className = 'announcement-bar__marquee-track';
    group.parentElement.insertBefore(track, group);
    track.appendChild(group);
    this.#marqueeTrack = track;
    this.#marqueeGroup = group;

    this.#fillMarquee();

    // Text width changes once webfonts load — re-measure so the loop stays seamless.
    document.fonts?.ready.then(() => this.#fillMarquee());

    // Re-fill on width change so the strip always overfills the bar.
    this.#resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.#fillFrame);
      this.#fillFrame = requestAnimationFrame(() => this.#fillMarquee());
    });
    this.#resizeObserver.observe(this);

    // Pause on hover.
    this.addEventListener('mouseenter', () => this.#marqueeAnimation?.pause());
    this.addEventListener('mouseleave', () => {
      if (this.#marqueeAnimation && !this.#reducedMotion) this.#marqueeAnimation.play();
    });
  }

  /**
   * Rebuilds the marquee: clones the announcement group until the strip is at least one
   * group wider than the bar, then drives a pixel-exact Web Animation that shifts the track
   * by exactly one group width and loops. Because every group is identical and sits exactly
   * one group-width apart, the reset back to 0 is invisible — a true seamless loop with no
   * percentage rounding and nothing changing mid-animation.
   */
  #fillMarquee() {
    const track = this.#marqueeTrack;
    const group = this.#marqueeGroup;
    if (!track || !group) return;

    // Rebuild from a single group so re-fills never stack clones.
    track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

    const groupWidth = group.offsetWidth;
    const containerWidth = this.offsetWidth;
    if (!groupWidth) return;

    // Enough groups that the strip stays covering the bar after shifting one group along.
    const totalGroups = Math.max(2, Math.ceil((containerWidth + groupWidth) / groupWidth));
    for (let i = 1; i < totalGroups; i++) {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-marquee-clone', '');
      clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
      track.appendChild(clone);
    }

    this.#marqueeAnimation?.cancel();

    if (this.#reducedMotion) return;

    // One group width per configured duration — constant speed no matter how many copies.
    this.#marqueeAnimation = track.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-groupWidth}px)` }],
      { duration: this.#marqueeDuration, iterations: Infinity, easing: 'linear' }
    );
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #marqueeDuration() {
    const seconds = parseFloat(getComputedStyle(this).getPropertyValue('--marquee-duration')) || 25;
    return seconds * 1000;
  }

  /** @type {HTMLElement | undefined} */
  #marqueeTrack;
  /** @type {HTMLElement | undefined} */
  #marqueeGroup;
  /** @type {Animation | undefined} */
  #marqueeAnimation;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {number | undefined} */
  #fillFrame;

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
